import { combineReducers, configureStore } from '@reduxjs/toolkit'
import { persistReducer, persistStore } from 'redux-persist'
import storage from 'redux-persist/es/storage'

import { affiliateSlice } from './affiliate/affiliate.slice'
import { clientAreaSlice } from './clientarea/clientarea.slice'
import { clientsSlice } from './clients/clients.slice'
import { complianceSlice } from './compliance/compliance.slice'
import { documentsSlice } from './documents/documents.slice'
import { gridSlice } from './grid/grid.slice'
import { identitySlice } from './identity/identity.slice'
import { marketingSlice } from './marketing/marketing.slice'
import { nPaymentsSlice } from './nPayments/nPayments.slice'
import { partnershipsSlice } from './partnerships/partnerships.slice'
import { ticketsSlice } from './tickets/tickets.slice'
import { tradingAccountsSlice } from './trading-accounts/trading-accounts.slice'
import { transactionSlice } from './transactions/transaction.slice'
import { walletSlice } from './wallet/wallet.slice'

export const QA_MODE_KEY = 'tickmill_qa_mode'

export enum QAModeState {
  Enabled = 'enabled',
  Disabled = 'disabled',
}

export const isQAMode = (): boolean => {
  try {
    return localStorage.getItem(QA_MODE_KEY) === QAModeState.Enabled
  } catch {
    return false
  }
}

const noopStorage = {
  getItem: (_key: string) => Promise.resolve(null),
  setItem: (_key: string, _value: string) => Promise.resolve(),
  removeItem: (_key: string) => Promise.resolve(),
}

export const PREFERRED_DATE_FORMAT = 'PREFERRED_DATE_FORMAT'
export const PREFERRED_TIME_ZONE = 'PREFERRED_TIME_ZONE'
export const DEFAULT_TIME_ZONE = 'Universal'

const rootReducer = combineReducers({
  clients: clientsSlice.reducer,
  tradingAccounts: tradingAccountsSlice.reducer,
  partnerships: partnershipsSlice.reducer,
  tickets: ticketsSlice.reducer,
  identity: identitySlice.reducer,
  documents: documentsSlice.reducer,
  transactions: transactionSlice.reducer,
  grid: gridSlice.reducer,
  wallet: walletSlice.reducer,
  compliance: complianceSlice.reducer,
  clientArea: clientAreaSlice.reducer,
  affiliates: affiliateSlice.reducer,
  nPayments: nPaymentsSlice.reducer,
  marketing: marketingSlice.reducer,
})

const persistConfig = {
  key: 'root',
  storage: isQAMode() ? noopStorage : storage,
  blacklist: ['grid', 'documents', 'transactions'],
  writeFailHandler: (err: Error) => {
    if (err.message.includes('QuotaExceededError')) {
      console.warn('LocalStorage quota exceeded, clearing old data...')
      try {
        storage.removeItem('persist:root')
      } catch (e) {
        console.error('Failed to clear storage:', e)
      }
    }
  },
}

const persistedReducer = persistReducer(persistConfig, rootReducer)

const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) => getDefaultMiddleware(),
})

const persistor = persistStore(store)

export type RootState = ReturnType<typeof rootReducer>
export type AppDispatch = typeof store.dispatch

export { store, persistor }
